import React, { useEffect, useState } from "react";
import { getSession } from "@/lib/authApi";

// Manage Billing overlay — shown when the signed-in user taps "Manage billing"
// on the UploadView topbar. Matches approved mockup Screen 5.
// Data flows:
//   GET  /api/auth/subscription/status
//   POST /api/auth/subscription/portal    (returns Stripe Billing Portal URL)
//   POST /api/auth/subscription/cancel    (cancel at period end)
//   POST /api/auth/subscription/resume    (undo cancel)

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function api(path, opts = {}) {
  const s = getSession();
  const res = await fetch(`${BACKEND}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(s?.token ? { Authorization: `Bearer ${s.token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  let json;
  try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }
  if (!res.ok) {
    throw new Error(String(json.detail || json.error || `HTTP_${res.status}`));
  }
  return json;
}

function formatUnixDate(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts * 1000);
    return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

function formatIsoDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function ManageBilling({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sub, setSub] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    setLoading(true);
    setError("");
    api("/api/auth/subscription/status")
      .then((r) => { if (!ignore) setSub(r); })
      .catch((e) => { if (!ignore) setError(String(e.message || e)); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [open]);

  async function openPortal() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await api("/api/auth/subscription/portal", {
        method: "POST",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (r.url) window.location.href = r.url;
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function cancelNow() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/subscription/cancel", { method: "POST" });
      const r = await api("/api/auth/subscription/status");
      setSub(r);
      setConfirmCancel(false);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/subscription/resume", { method: "POST" });
      const r = await api("/api/auth/subscription/status");
      setSub(r);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const nextChargeLabel = sub?.cancel_at_period_end
    ? `Ends ${formatUnixDate(sub.current_period_end)}`
    : formatUnixDate(sub?.current_period_end);

  const statusPill = (() => {
    if (!sub) return null;
    if (!sub.has_subscription) return { label: "Admin-issued license", tone: "neutral" };
    if (sub.cancel_at_period_end) return { label: `Cancels ${formatUnixDate(sub.current_period_end)}`, tone: "warn" };
    const s = String(sub.status || "").toLowerCase();
    if (s === "active") return { label: `Active · Renews ${formatUnixDate(sub.current_period_end)}`, tone: "ok" };
    if (s === "past_due") return { label: "Payment past due", tone: "warn" };
    if (s === "canceled") return { label: "Canceled", tone: "err" };
    if (s === "trialing") return { label: "Free trial", tone: "ok" };
    return { label: s || "Unknown", tone: "neutral" };
  })();

  return (
    <div className="mb-overlay" data-testid="manage-billing-overlay" onClick={onClose}>
      <style>{styles}</style>
      <div className="mb-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="mb-header">
          <div className="mb-title">
            Manage <em>billing</em>
          </div>
          <button
            className="mb-close"
            onClick={onClose}
            data-testid="manage-billing-close-btn"
            aria-label="Close"
          >×</button>
        </div>

        {loading ? (
          <div className="mb-loading">
            <div className="mb-spinner" />
            <div className="mb-loading-label">Loading subscription…</div>
          </div>
        ) : error ? (
          <div className="pa-error mb-error" data-testid="manage-billing-error">{error}</div>
        ) : sub ? (
          <>
            {statusPill && (
              <div className={`mb-status mb-status-${statusPill.tone}`} data-testid="manage-billing-status">
                <span className="mb-dot" />
                {statusPill.label}
              </div>
            )}

            <div className="mb-card">
              <Row k="Plan" v={sub.has_subscription
                ? `Analyzer · $9.99/day`
                : "Legacy license"} />
              <Row k="Next charge" v={sub.has_subscription
                ? `${nextChargeLabel} · $${(sub.amount || 0).toFixed(2)} ${(sub.currency || "").toUpperCase()}`
                : "—"} />
              <Row k="Method" v={sub.card_brand
                ? `${cap(sub.card_brand)} •••• ${sub.card_last4}${sub.card_exp ? ` · ${sub.card_exp}` : ""}`
                : "—"} />
              <Row k="Pharmacy" v={sub.pharmacy_name || "—"} last />
              <Row k="Since" v={formatIsoDate(sub.since)} last />
            </div>

            {sub.has_subscription ? (
              <>
                <button
                  type="button"
                  className="mb-cta mb-outline"
                  onClick={openPortal}
                  disabled={busy}
                  data-testid="manage-billing-portal-btn"
                >
                  Update payment method / view invoices
                </button>

                {sub.cancel_at_period_end ? (
                  <button
                    type="button"
                    className="mb-cta mb-outline"
                    onClick={resume}
                    disabled={busy}
                    data-testid="manage-billing-resume-btn"
                  >
                    Resume subscription
                  </button>
                ) : confirmCancel ? (
                  <div className="mb-confirm" data-testid="manage-billing-cancel-confirm">
                    <div className="mb-confirm-title">Cancel subscription?</div>
                    <div className="mb-confirm-body">
                      You&rsquo;ll keep access until {formatUnixDate(sub.current_period_end)}.
                      No further charges after that.
                    </div>
                    <div className="mb-confirm-btns">
                      <button
                        type="button"
                        className="mb-cta mb-danger"
                        onClick={cancelNow}
                        disabled={busy}
                        data-testid="manage-billing-cancel-confirm-btn"
                      >
                        {busy ? "…" : "Yes, cancel"}
                      </button>
                      <button
                        type="button"
                        className="mb-cta mb-outline"
                        onClick={() => setConfirmCancel(false)}
                        disabled={busy}
                      >
                        Keep subscription
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="mb-cta mb-danger"
                    onClick={() => setConfirmCancel(true)}
                    disabled={busy}
                    data-testid="manage-billing-cancel-btn"
                  >
                    Cancel subscription
                  </button>
                )}
              </>
            ) : (
              <div className="mb-legacy-note">
                {sub.note ? sub.note : (
                  <>This license was issued directly by an admin — no Stripe
                  subscription is attached. Contact support if you need billing
                  changes.</>
                )}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function Row({ k, v, last }) {
  return (
    <div className={`mb-row ${last ? "mb-row-last" : ""}`}>
      <span className="mb-k">{k}</span>
      <span className="mb-v">{v}</span>
    </div>
  );
}

function cap(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const styles = `
.mb-overlay {
  position: fixed; inset: 0;
  background: rgba(15, 40, 30, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 1000;
  display: flex; align-items: flex-end; justify-content: center;
  animation: mb-fade 0.2s ease-out;
}
@keyframes mb-fade { from { opacity: 0; } to { opacity: 1; } }
.mb-sheet {
  background: #F7F3EC;
  color: #1B3B2E;
  border-radius: 24px 24px 0 0;
  padding: 22px 24px calc(28px + env(safe-area-inset-bottom));
  width: 100%;
  max-width: 460px;
  max-height: 88vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  font-family: 'Geist', 'Inter', sans-serif;
  animation: mb-slide 0.22s ease-out;
  box-shadow: 0 -12px 40px rgba(15, 40, 30, 0.25);
}
@media (min-width: 700px) {
  .mb-overlay { align-items: center; }
  .mb-sheet { border-radius: 24px; max-height: 82vh; }
}
@keyframes mb-slide {
  from { transform: translateY(24px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
.mb-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 14px;
}
.mb-title {
  font-family: 'Fraunces', serif;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: -0.02em;
}
.mb-title em { font-style: italic; color: #2B5E4A; }
.mb-close {
  background: transparent;
  border: none;
  color: #5D4A28;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background 0.12s;
}
.mb-close:hover { background: rgba(27,59,46,0.06); }

.mb-status {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 14px;
}
.mb-status .mb-dot {
  width: 6px; height: 6px; border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(255,255,255,0.4);
}
.mb-status-ok {
  background: rgba(43,94,74,0.10); color: #2B5E4A;
}
.mb-status-ok .mb-dot { background: #2B5E4A; }
.mb-status-warn {
  background: rgba(202,132,20,0.14); color: #7A4E0E;
}
.mb-status-warn .mb-dot { background: #B57414; }
.mb-status-err {
  background: rgba(134,47,26,0.14); color: #862F1A;
}
.mb-status-err .mb-dot { background: #862F1A; }
.mb-status-neutral {
  background: rgba(93,74,40,0.10); color: #5D4A28;
}
.mb-status-neutral .mb-dot { background: #9B8666; }

.mb-card {
  border: 1px solid rgba(27,59,46,0.14);
  border-radius: 16px;
  background: rgba(255,253,247,0.6);
  padding: 4px 18px;
  margin-bottom: 16px;
}
.mb-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(27,59,46,0.08);
  font-size: 13px;
  gap: 12px;
}
.mb-row-last { border-bottom: none; }
.mb-k { color: #7A6947; }
.mb-v { color: #1B3B2E; font-weight: 500; text-align: right; }

.mb-cta {
  display: block;
  width: 100%;
  padding: 13px 18px;
  border-radius: 100px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 10px;
  border: none;
  transition: opacity 0.15s, transform 0.05s, background 0.15s;
}
.mb-cta:disabled { opacity: 0.55; cursor: default; }
.mb-cta:active:not(:disabled) { transform: scale(0.98); }

.mb-outline {
  background: transparent;
  color: #1B3B2E;
  border: 1px solid rgba(27,59,46,0.28);
}
.mb-outline:hover:not(:disabled) { background: rgba(27,59,46,0.04); }

.mb-danger {
  background: transparent;
  color: #862F1A;
  border: 1px solid rgba(134,47,26,0.32);
}
.mb-danger:hover:not(:disabled) { background: rgba(134,47,26,0.06); }

.mb-confirm {
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(134,47,26,0.06);
  border: 1px solid rgba(134,47,26,0.20);
}
.mb-confirm-title {
  font-family: 'Fraunces', serif;
  font-style: italic;
  color: #862F1A;
  font-size: 15px;
  margin-bottom: 4px;
}
.mb-confirm-body {
  font-size: 12px;
  color: #5D4A28;
  line-height: 1.5;
  margin-bottom: 10px;
}
.mb-confirm-btns { display: flex; flex-direction: column; gap: 8px; }
.mb-confirm-btns .mb-cta { margin-top: 0; }

.mb-loading { text-align: center; padding: 30px 0; }
.mb-loading-label { font-size: 12.5px; color: #5D4A28; margin-top: 12px; }
.mb-spinner {
  width: 32px; height: 32px;
  margin: 0 auto;
  border: 3px solid rgba(43,94,74,0.15);
  border-top-color: #2B5E4A;
  border-radius: 50%;
  animation: mb-spin 0.8s linear infinite;
}
@keyframes mb-spin { to { transform: rotate(360deg); } }

.mb-error {
  margin-top: 8px;
}
.mb-legacy-note {
  font-size: 12px;
  color: #7A6947;
  line-height: 1.5;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(93,74,40,0.08);
  margin-top: 4px;
}
`;
